import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Short section label above the title, e.g. the menu group. */
  eyebrow?: ReactNode;
  /** Buttons on the right: the page's own actions, primary one last. */
  actions?: ReactNode;
  /** Figures or filters shown under the title row. */
  children?: ReactNode;
  /** "section" for a header inside a tab, under the page's own title. */
  level?: "page" | "section";
  className?: string;
}

/**
 * The one page header of the back-office spaces: a single title size, an
 * optional description and the page's actions. Every admin, moderator and
 * accountant page starts with it so they all read the same way.
 */
export function PageHeader({ title, description, eyebrow, actions, children, level = "page", className }: PageHeaderProps) {
  const Heading = level === "page" ? "h1" : "h2";
  return (
    <header className={cn("space-y-5 border-b border-border/60 pb-6", className)}>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0 space-y-1.5">
          {eyebrow ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-faint-foreground">{eyebrow}</p>
          ) : null}
          <Heading className={cn("font-semibold tracking-tight text-foreground", level === "page" ? "text-2xl" : "text-lg")}>{title}</Heading>
          {description ? <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:flex-nowrap md:justify-end">{actions}</div> : null}
      </div>
      {children}
    </header>
  );
}

export interface StatItem {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** Colours the value: use "attention" for things waiting on someone. */
  tone?: "default" | "attention" | "success" | "danger";
}

const toneClass: Record<NonNullable<StatItem["tone"]>, string> = {
  default: "text-foreground",
  attention: "text-warning",
  success: "text-success",
  danger: "text-danger",
};

/** A row of key figures, bordered cells with tabular numbers. */
export function StatStrip({ items, className }: { items: StatItem[]; className?: string }) {
  // Hairlines come from a 1px gap over the border colour, so they stay right
  // whatever the number of cells and however the grid wraps.
  const columns = items.length === 1 ? "grid-cols-1" : items.length === 3 ? "grid-cols-3" : "grid-cols-2";
  return (
    <dl
      className={cn(
        "grid gap-px overflow-hidden rounded-lg border border-border/60 bg-border/60",
        columns,
        items.length >= 4 && "lg:grid-cols-4",
        className,
      )}
    >
      {items.map((item, index) => (
        <div key={index} className="min-w-0 bg-surface px-4 py-3">
          <dt className="truncate text-xs text-muted-foreground">{item.label}</dt>
          <dd className={cn("mt-1 truncate text-lg font-semibold tabular-nums", toneClass[item.tone ?? "default"])}>{item.value}</dd>
          {item.hint ? <dd className="mt-0.5 truncate text-xs text-faint-foreground">{item.hint}</dd> : null}
        </div>
      ))}
    </dl>
  );
}
